import React, {useMemo} from 'react'
import {Platform, ScrollView, useWindowDimensions, View} from 'react-native'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useFocusEffect, useNavigation} from '@react-navigation/native'
import {type NativeStackNavigationProp} from '@react-navigation/native-stack'
import {useQuery} from '@tanstack/react-query'

import {DISCOVER_FEED_URI} from '#/lib/constants'
import {getPostVideo} from '#/lib/feed/mediaHelpers'
import {type CommonNavigatorParams} from '#/lib/routes/types'
import {getSafeUiLabel} from '#/lib/strings/ui-labels'
import {isTVSegmentPost, isTVVideoPage} from '#/lib/tv/tvParser'
import {
  getContinueWatchingEntries,
  loadWatchHistory,
  subscribeWatchHistory,
} from '#/lib/tv/watchHistory'
import {useGetPosts} from '#/state/queries/post'
import {usePostFeedQuery} from '#/state/queries/post-feed'
import {useRequireAuth} from '#/state/session'
import {atoms as a, useTheme, web} from '#/alf'
import {Button} from '#/components/Button'
import {CirclePlus_Stroke2_Corner0_Rounded as UploadIcon} from '#/components/icons/CirclePlus'
import {TellusTVCard} from '#/components/tv/TellusTVCard'
import {TellusTVShelf} from '#/components/tv/TellusTVShelf'
import {Text} from '#/components/Typography'

function flattenFeedPosts(data: ReturnType<typeof usePostFeedQuery>['data']) {
  const posts = []
  if (!data?.pages) return posts
  for (const page of data.pages) {
    for (const slice of page.slices) {
      for (const item of slice.items) {
        posts.push(item.post)
      }
    }
  }
  return posts
}

export function TellusTVScreen() {
  const t = useTheme()
  const {_} = useLingui()
  const navigation =
    useNavigation<NativeStackNavigationProp<CommonNavigatorParams>>()
  const requireAuth = useRequireAuth()
  const getPosts = useGetPosts()
  const {width} = useWindowDimensions()

  const discoverFeedDesc = `feedgen|${DISCOVER_FEED_URI}`
  const {data: discoverData, isLoading: discoverLoading} =
    usePostFeedQuery(discoverFeedDesc)
  const {data: followingData, isLoading: followingLoading} =
    usePostFeedQuery('following')

  const [historyState, setHistoryState] = React.useState<Awaited<
    ReturnType<typeof loadWatchHistory>
  > | null>(null)

  useFocusEffect(
    React.useCallback(() => {
      let unsub: (() => void) | undefined
      let active = true
      loadWatchHistory().then(state => {
        if (active) setHistoryState(state)
      })
      unsub = subscribeWatchHistory(() => {
        loadWatchHistory().then(state => {
          if (active) setHistoryState(state)
        })
      })
      return () => {
        active = false
        unsub?.()
      }
    }, []),
  )

  const continueEntries = useMemo(() => {
    if (!historyState) return []
    return getContinueWatchingEntries(historyState)
  }, [historyState])

  const continueUris = useMemo(
    () => continueEntries.map(entry => entry.videoPageUri),
    [continueEntries],
  )

  const {data: continuePosts} = useQuery({
    queryKey: ['tellustv-continue-posts', continueUris.join('|')],
    queryFn: () => getPosts({uris: continueUris}),
    enabled: continueUris.length > 0,
  })

  const recommendedPosts = useMemo(() => {
    const source = discoverData?.pages?.length ? discoverData : followingData
    const posts = flattenFeedPosts(source)
    const filtered = posts.filter(post => {
      if (isTVSegmentPost(post)) return false
      return isTVVideoPage(post) || Boolean(getPostVideo(post)?.url)
    })
    const deduped = new Map(filtered.map(post => [post.uri, post]))
    const unique = Array.from(deduped.values())
    if (!historyState) return unique
    const now = Date.now()
    const scored = unique.map(post => {
      const indexedAt = new Date(post.indexedAt).getTime()
      const recencyHours = (now - indexedAt) / 36e5
      const recencyScore = Math.max(0, 48 - recencyHours)
      const followBoost = post.author.viewer?.following ? 10 : 0
      const likedBoost = post.viewer?.like ? 4 : 0
      const authorWatchMs = historyState.authorWatchMs[post.author.did] || 0
      const watchBoost = Math.min(8, authorWatchMs / 600000)
      const watchedPenalty = historyState.entries[post.uri] ? -6 : 0
      return {
        post,
        score:
          recencyScore + followBoost + likedBoost + watchBoost + watchedPenalty,
      }
    })
    return scored.sort((a, b) => b.score - a.score).map(item => item.post)
  }, [discoverData, followingData, historyState])

  const contentPadding = width < 640 ? 12 : width < 1024 ? 16 : 24
  const contentMaxWidth = 1280
  const leftNavWidth = width >= 1024 ? 240 : 0
  const availableWidth = Math.max(0, width - leftNavWidth - contentPadding * 2)
  const contentWidth = Math.min(availableWidth, contentMaxWidth)
  const gridColumns = useMemo(() => {
    if (contentWidth >= 1200) return 3
    if (contentWidth >= 860) return 2
    return 1
  }, [contentWidth])
  const gridGap = width < 640 ? 12 : 16
  const rawCardWidth =
    gridColumns > 1
      ? (contentWidth - gridGap * (gridColumns - 1)) / gridColumns
      : contentWidth
  const cardWidth = Math.max(240, Math.min(360, Math.floor(rawCardWidth)))
  const maxRecs = gridColumns >= 3 ? 6 : gridColumns === 2 ? 6 : 4
  const useCssGrid = Platform.OS === 'web'
  const gridCardWidth = useCssGrid ? '100%' : cardWidth
  const chipLabels = useMemo(
    () => [
      _(msg`All`),
      _(msg`Music`),
      _(msg`Gaming`),
      _(msg`Science`),
      _(msg`Tech`),
      _(msg`Sports`),
      _(msg`Live`),
      _(msg`Podcasts`),
    ],
    [_],
  )
  const safeChipLabels = useMemo(() => {
    const safe: string[] = []
    for (const label of chipLabels) {
      const safeLabel = getSafeUiLabel(label, {
        context: 'tellustv-chip',
      })
      if (!safeLabel) continue
      safe.push(safeLabel)
    }
    return safe
  }, [chipLabels])
  return (
    <View testID="tellusTVScreen" style={[a.flex_1, t.atoms.bg]}>
      <ScrollView
        contentContainerStyle={[
          {
            paddingHorizontal: contentPadding,
            paddingBottom: 32,
            gap: 24,
            maxWidth: contentMaxWidth,
            alignSelf: 'center',
            width: '100%',
          },
        ]}
        showsVerticalScrollIndicator={false}>
        <View
          style={[a.flex_row, a.align_center, a.justify_between, {height: 56}]}>
          <Text style={[a.text_2xl, a.font_bold]}>Tellus TV</Text>
          <Button
            variant="ghost"
            color="secondary"
            size="small"
            shape="round"
            label={_(msg`Upload`)}
            accessibilityLabel={_(msg`Upload`)}
            accessibilityHint={_(msg`Upload a video to Tellus TV`)}
            style={[
              a.flex_row,
              a.align_center,
              a.gap_xs,
              {
                height: 36,
                paddingHorizontal: width >= 640 ? 12 : 10,
                borderRadius: 999,
                backgroundColor: t.atoms.bg_contrast_25.backgroundColor,
              },
            ]}
            hoverStyle={[
              {
                backgroundColor: t.atoms.bg_contrast_50.backgroundColor,
              },
            ]}
            onPress={() =>
              requireAuth(() => navigation.navigate('TellusTVUpload'))
            }>
            <View style={[a.flex_row, a.align_center, a.gap_xs]}>
              <UploadIcon size="sm" fill={t.palette.contrast_700} />
              {width >= 640 ? (
                <Text style={[a.text_sm]}>{_(msg`Upload`)}</Text>
              ) : null}
            </View>
          </Button>
        </View>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={[a.flex_row, a.gap_sm]}>
          {safeChipLabels.map(label => (
            <View
              key={label}
              style={[
                a.rounded_full,
                a.px_md,
                {height: 32},
                a.align_center,
                a.justify_center,
                {backgroundColor: t.atoms.bg_contrast_25.backgroundColor},
              ]}>
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                {label}
              </Text>
            </View>
          ))}
        </ScrollView>

        <View style={[a.gap_md]}>
          <Text style={[a.text_lg, a.font_bold]}>
            {_(msg`Recommandations`)}
          </Text>
          <View style={[a.rounded_md, a.p_md, t.atoms.bg_contrast_25]}>
            {recommendedPosts.length ? (
              <View
                style={[
                  a.flex_row,
                  a.flex_wrap,
                  {gap: gridGap},
                  web({
                    display: 'grid',
                    gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
                    gap: gridGap,
                  }),
                ]}>
                {recommendedPosts.slice(0, maxRecs).map(post => (
                  <TellusTVCard
                    key={post.uri}
                    post={post}
                    width={gridCardWidth}
                    variant="grid"
                  />
                ))}
              </View>
            ) : discoverLoading || followingLoading ? (
              <View style={[a.flex_row, a.flex_wrap, {gap: gridGap}]}>
                {Array.from({length: Math.max(2, gridColumns)}).map(
                  (_, idx) => (
                    <View
                      key={idx}
                      style={[
                        a.rounded_md,
                        t.atoms.bg_contrast_25,
                        {width: cardWidth, height: cardWidth * 0.7},
                      ]}
                    />
                  ),
                )}
              </View>
            ) : (
              <View>
                <Text style={[t.atoms.text_contrast_medium]}>
                  {_(
                    msg`Follow creators to start building your Tellus TV shelves.`,
                  )}
                </Text>
              </View>
            )}
          </View>
        </View>

        <View style={[a.gap_md]}>
          {continueEntries.length ? (
            <View style={[a.rounded_md, a.p_md, t.atoms.bg_contrast_25]}>
              <TellusTVShelf
                title={_(msg`Continue Watching`)}
                itemsCount={continueEntries.length}>
                {continuePosts?.map(post => {
                  const entry = continueEntries.find(
                    e => e.videoPageUri === post.uri,
                  )
                  const ratio =
                    entry && entry.totalDurationMs
                      ? entry.positionMs / entry.totalDurationMs
                      : undefined
                  return (
                    <TellusTVCard
                      key={post.uri}
                      post={post}
                      progressRatio={ratio}
                      variant="compact"
                      width={320}
                    />
                  )
                })}
              </TellusTVShelf>
            </View>
          ) : (
            <View
              style={[a.rounded_md, a.px_lg, a.py_md, t.atoms.bg_contrast_25]}>
              <Text style={[t.atoms.text_contrast_medium]}>
                {_(msg`Start watching to see your progress here.`)}
              </Text>
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  )
}
